import { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { publicApi, api } from '../lib/api';
import Header from '../components/Header';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { Radio, MapPin, Quote, TrendingUp, Play, ChevronLeft, ChevronRight, History, Sparkles, Users, Star } from 'lucide-react';
import { PATNA_QUOTES } from '../lib/patna';
import CassetteFeatured from '../components/CassetteFeatured';

const CHIP_FILTERS = [
  { key: 'all', label: 'Sab' },
  { key: 'featured', label: 'Chuninda' },
  { key: 'era', label: 'Daur' },
  { key: 'mood', label: 'Mood' },
];

const Home = () => {
  const [playlists, setPlaylists] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [trendingWeek, setTrendingWeek] = useState([]);
  const [trendingAll, setTrendingAll] = useState([]);
  const [history, setHistory] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [artists, setArtists] = useState([]);
  const [chip, setChip] = useState('all');
  const [trendWindow, setTrendWindow] = useState('week');
  const [loading, setLoading] = useState(true);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const { playQueue, playRandom, currentSong } = usePlayer();
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      publicApi.listPlaylists(),
      api.get('/featured').then((r) => r.data).catch(() => []),
      api.get('/trending?window=week&limit=10').then((r) => r.data).catch(() => []),
      api.get('/trending?window=all&limit=10').then((r) => r.data).catch(() => []),
      api.get('/recently-added?limit=12').then((r) => r.data).catch(() => []),
      api.get('/artists?limit=8').then((r) => r.data).catch(() => []),
    ]).then(([pls, feat, tw, ta, freshList, arts]) => {
      setPlaylists(pls);
      setFeatured(feat);
      setTrendingWeek(tw);
      setTrendingAll(ta);
      setFresh(freshList);
      setArtists(arts);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setHistory([]); return; }
    api.get('/me/history?limit=25').then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, [user]);

  useEffect(() => {
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % PATNA_QUOTES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const eras = playlists.filter((p) => p.category_type === 'era');
  const moods = playlists.filter((p) => p.category_type === 'mood' && !p.featured);
  const trending = trendWindow === 'week' ? trendingWeek : trendingAll;
  const currentQuote = PATNA_QUOTES[quoteIdx];

  const showFeaturedSection = chip === 'all' || chip === 'featured';
  const showEraSection = chip === 'all' || chip === 'era';
  const showMoodSection = chip === 'all' || chip === 'mood';

  return (
    <div className="min-h-screen pb-32">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6" data-testid="home-page">
        {/* Hero */}
        <section className="fade-in-up">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFB800] mb-2">
            {user
              ? <>Namaste, {user.name?.split(' ')[0] || 'friend'}</>
              : <>Phir Mulakat</>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FFF3E0] leading-none drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]">
            Tonight's a<br />
            <span className="text-[#FF007F]">retro affair</span>.
          </h1>
          <p className="text-sm sm:text-base text-[#FFF3E0]/85 mt-3 max-w-md font-body">
            Handpicked Hindi classics, sorted by the era they defined and the mood they carry.
          </p>
          {!currentSong && (
            <button
              data-testid="start-vibe-btn"
              onClick={() => playRandom({ expand: false })}
              className="mt-5 h-12 px-5 rounded-full bg-[#FFB800] text-[#120505] font-bold flex items-center gap-2 hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-[0_6px_24px_rgba(255,184,0,0.35)] animate-pulse-slow"
              style={{ animation: 'fadeInUp 0.6s ease-out both' }}
            >
              <Play className="w-4 h-4 fill-current" />
              Start the vibe
            </button>
          )}
        </section>

        {/* Chip filters */}
        <section className="mt-7 -mx-4 sm:mx-0 px-4 sm:px-0" data-testid="chip-filters">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {CHIP_FILTERS.map((c) => (
              <button
                key={c.key}
                data-testid={`chip-${c.key}`}
                onClick={() => setChip(c.key)}
                className={`shrink-0 h-9 px-4 rounded-full text-sm font-body transition-all whitespace-nowrap ${
                  chip === c.key
                    ? 'bg-[#FFB800] text-[#120505] font-bold shadow-[0_4px_16px_rgba(255,184,0,0.3)]'
                    : 'border border-[#3D1A1A] text-[#BFA5A5] hover:text-[#FFF3E0] hover:border-[#FFB800]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </section>

        {/* Featured rotator */}
        {showFeaturedSection && featured.length > 0 && <CassetteFeatured items={featured} />}

        {/* Aap ne suna — history (signed-in only) */}
        {user && history.length > 0 && (
          <SongCarousel
            testId="history-section"
            icon={<History className="w-5 h-5 text-[#FFB800]" />}
            title="Aap Ne Suna"
            songs={history}
            onPlay={(list, i) => playQueue(list, i)}
          />
        )}

        {/* Fresh Off the Turntable */}
        {fresh.length > 0 && (
          <SongCarousel
            testId="fresh-section"
            icon={<Sparkles className="w-5 h-5 text-[#FF007F]" />}
            title="Taaza Beats"
            songs={fresh}
            onPlay={(list, i) => playQueue(list, i)}
            variant="tall"
          />
        )}

        {/* Trending */}
        <section className="mt-10" data-testid="trending-section">
          <div className="flex items-baseline gap-3 mb-2 flex-wrap">
            <TrendingUp className="w-5 h-5 text-[#FF007F]" />
            <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">Patna Mein Dhoom</h2>
          </div>
          <div className="flex gap-2 mb-4">
            <button
              data-testid="trend-week-btn"
              onClick={() => setTrendWindow('week')}
              className={`px-3 py-1 rounded-full text-xs font-body ${trendWindow === 'week' ? 'bg-[#FFB800] text-[#120505] font-bold' : 'border border-[#3D1A1A] text-[#BFA5A5] hover:text-[#FFF3E0]'}`}
            >
              This Week
            </button>
            <button
              data-testid="trend-all-btn"
              onClick={() => setTrendWindow('all')}
              className={`px-3 py-1 rounded-full text-xs font-body ${trendWindow === 'all' ? 'bg-[#FFB800] text-[#120505] font-bold' : 'border border-[#3D1A1A] text-[#BFA5A5] hover:text-[#FFF3E0]'}`}
            >
              All Time
            </button>
          </div>
          {trending.length === 0 ? (
            <EmptyStrip text="Kuch gaane bajaiye, yahaan aa jaayenge." />
          ) : (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1" data-testid="trending-list">
              {trending.map((s, i) => (
                <TrendingCard key={s.id + trendWindow} song={s} index={i} testId={`trending-song-${i}`} onClick={() => playQueue(trending, i)} />
              ))}
            </div>
          )}
        </section>

        {/* Rotating quote strip */}
        <QuoteStrip idx={quoteIdx} setIdx={setQuoteIdx} quote={currentQuote} />

        {/* Explore by Artist */}
        {artists.length > 0 && (
          <section className="mt-10" data-testid="artists-section">
            <div className="flex items-baseline gap-3 mb-4">
              <Users className="w-5 h-5 text-[#FFB800]" />
              <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">Kalakaar</h2>
              <div className="flex-1 h-px bg-[#3D1A1A]" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="artists-grid">
              {artists.map((a, i) => <ArtistTile key={a.name} artist={a} onPick={async (list) => list.length && playQueue(list, 0)} testId={`artist-${i}`} />)}
            </div>
          </section>
        )}

        {/* By Era */}
        {showEraSection && (
          <section className="mt-12" data-testid="era-section">
            <div className="flex items-baseline gap-3 mb-4">
              <Radio className="w-5 h-5 text-[#FFB800]" />
              <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">Daur Ke Hisaab Se</h2>
              <div className="flex-1 h-px bg-[#3D1A1A]" />
            </div>
            {loading ? <SkeletonGrid /> : (
              <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="era-grid">
                {eras.map((pl, i) => <PlaylistCard key={pl.id} playlist={pl} span={eras.length === 1 || i === 0 ? 2 : 1} />)}
              </div>
            )}
          </section>
        )}

        {/* By Mood */}
        {showMoodSection && (
          <section className="mt-12" data-testid="mood-section">
            <div className="flex items-baseline gap-3 mb-4">
              <MapPin className="w-5 h-5 text-[#FF007F]" />
              <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">Mood Ke Hisaab Se</h2>
              <div className="flex-1 h-px bg-[#3D1A1A]" />
            </div>
            {loading ? <SkeletonGrid /> : (
              <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="mood-grid">
                {moods.map((pl) => <PlaylistCard key={pl.id} playlist={pl} />)}
              </div>
            )}
          </section>
        )}

        <div className="mt-16 text-center">
          <div className="inline-block px-4 py-2 rounded-full border border-[#3D1A1A] text-xs uppercase tracking-[0.25em] text-[#BFA5A5]">
            Aur Bhi Aa Raha Hai ✦
          </div>
        </div>
      </main>
    </div>
  );
};

/* ---------------- Sub-components ---------------- */

const FeaturedRotator = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(timerRef.current);
  }, [items.length]);

  const goTo = (i) => {
    setIdx(((i % items.length) + items.length) % items.length);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((k) => (k + 1) % items.length), 6500);
  };

  const current = items[idx];
  if (!current) return null;

  return (
    <section className="mt-8 relative" data-testid="featured-rotator">
      <Link
        to={`/playlist/${current.slug}`}
        data-testid={`featured-slide-${current.slug}`}
        className="relative block rounded-2xl overflow-hidden border border-[#FFB800]/40 group h-44 sm:h-52"
      >
        {items.map((it, i) => (
          <img
            key={it.id}
            src={it.cover_image}
            alt={it.name}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: i === idx ? 0.55 : 0 }}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#120505] via-[#120505]/70 to-[#120505]/20" />
        <div className="relative h-full p-5 sm:p-6 flex items-center gap-4">
          <div className="flex-1 min-w-0" key={current.id}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-1 fade-in-up flex items-center gap-1">
              <Star className="w-3 h-3 fill-current" />
              Featured · चुनिंदा · {current.category_type === 'era' ? 'Era' : 'Mood'}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0] fade-in-up leading-tight">{current.name}</h2>
            <p className="text-xs text-[#FFF3E0]/80 mt-1 max-w-md line-clamp-2 fade-in-up">
              {current.description || `${current.song_ids?.length || 0} handpicked songs`}
            </p>
            <div className="text-[11px] text-[#FFB800] mt-2">{current.song_ids?.length || 0} songs · Tap to play</div>
          </div>
          <div className="w-14 h-14 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center shadow-[0_6px_20px_rgba(255,184,0,0.4)] shrink-0">
            <Play className="w-6 h-6 fill-current ml-1" />
          </div>
        </div>
      </Link>
      {items.length > 1 && (
        <>
          <button
            data-testid="featured-prev"
            onClick={(e) => { e.stopPropagation(); goTo(idx - 1); }}
            className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#120505]/80 backdrop-blur border border-[#3D1A1A] items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A]"
            aria-label="Previous featured"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            data-testid="featured-next"
            onClick={(e) => { e.stopPropagation(); goTo(idx + 1); }}
            className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#120505]/80 backdrop-blur border border-[#3D1A1A] items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A]"
            aria-label="Next featured"
          ><ChevronRight className="w-4 h-4" /></button>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                data-testid={`featured-dot-${i}`}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-[#FFB800]' : 'w-1.5 bg-[#3D1A1A] hover:bg-[#BFA5A5]'}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const SongCarousel = ({ testId, icon, title, songs, onPlay, variant = 'square' }) => {
  const w = variant === 'tall' ? 'w-32 sm:w-36' : 'w-36 sm:w-40';
  const aspect = variant === 'tall' ? 'aspect-[3/4]' : 'aspect-square';
  return (
    <section className="mt-10" data-testid={testId}>
      <div className="flex items-baseline gap-3 mb-4">
        {icon}
        <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">{title}</h2>
        <div className="flex-1 h-px bg-[#3D1A1A]" />
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {songs.map((s, i) => (
          <button
            key={s.id}
            data-testid={`${testId}-item-${i}`}
            onClick={() => onPlay(songs, i)}
            className={`shrink-0 ${w} text-left group`}
          >
            <div className={`relative w-full ${aspect} rounded-2xl overflow-hidden border border-[#3D1A1A]`}>
              <img src={s.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#120505]/80 to-transparent" />
              <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-[0_4px_12px_rgba(255,184,0,0.5)]">
                <Play className="w-4 h-4 fill-current ml-0.5" />
              </div>
            </div>
            <div className="mt-2 text-sm text-[#FFF3E0] truncate">{s.title}</div>
            <div className="text-xs text-[#BFA5A5] truncate">{s.artist}</div>
          </button>
        ))}
      </div>
    </section>
  );
};

const TrendingCard = ({ song, index, testId, onClick }) => (
  <button data-testid={testId} onClick={onClick} className="shrink-0 w-36 sm:w-40 text-left group">
    <div className="relative w-full aspect-square rounded-2xl overflow-hidden border border-[#3D1A1A]">
      <img src={song.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#120505]/80 to-transparent" />
      <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-[#FFB800] text-[#120505] font-black flex items-center justify-center text-sm shadow">{index + 1}</div>
      <div className="absolute top-2 right-2 px-2 h-6 rounded-full bg-[#120505]/80 backdrop-blur text-[10px] font-bold text-[#FFB800] flex items-center gap-1">
        <Play className="w-2.5 h-2.5 fill-current" /> {song.play_count}
      </div>
      <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[#FF007F] text-[#FFF3E0] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Play className="w-4 h-4 fill-current ml-0.5" />
      </div>
    </div>
    <div className="mt-2 text-sm text-[#FFF3E0] truncate">{song.title}</div>
    <div className="text-xs text-[#BFA5A5] truncate">{song.artist}</div>
  </button>
);

const ArtistTile = ({ artist, onPick, testId }) => {
  const initials = useMemo(() => artist.name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase(), [artist.name]);
  const handleClick = async () => {
    try {
      const { data } = await api.get(`/artists/${encodeURIComponent(artist.name)}`);
      onPick(data || []);
    } catch { onPick([]); }
  };
  return (
    <button data-testid={testId} onClick={handleClick} className="group relative rounded-2xl overflow-hidden border border-[#3D1A1A] p-3 hover:border-[#FFB800] transition-colors bg-[#220A0A]/40 backdrop-blur-sm flex items-center gap-3 text-left">
      <div className="w-14 h-14 rounded-full overflow-hidden border border-[#3D1A1A] shrink-0 relative bg-gradient-to-br from-[#FFB800] to-[#FF007F]">
        {artist.cover ? (
          <img src={artist.cover} alt={artist.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#120505] font-black text-lg">{initials || '?'}</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#FFF3E0] truncate">{artist.name}</div>
        <div className="text-[10px] text-[#BFA5A5] uppercase tracking-wider">{artist.song_count} songs</div>
      </div>
      <div className="w-8 h-8 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
      </div>
    </button>
  );
};

const QuoteStrip = ({ idx, setIdx, quote }) => (
  <section
    data-testid="quote-strip"
    className="mt-10 relative overflow-hidden rounded-2xl border border-[#3D1A1A] bg-[#220A0A]/60 backdrop-blur"
  >
    <div className="absolute -left-3 -top-3 w-24 h-24 rounded-full bg-[#FF007F]/10 blur-2xl" />
    <div className="relative flex items-start gap-3 p-4 sm:p-5">
      <Quote className="w-5 h-5 text-[#FFB800] shrink-0 mt-1" />
      <div className="flex-1 min-w-0" key={idx}>
        <div className="font-display text-lg sm:text-xl text-[#FFF3E0] fade-in-up" lang="hi">"{quote.hi}"</div>
      </div>
      <div className="hidden sm:flex flex-col gap-1 items-end shrink-0">
        {PATNA_QUOTES.map((_, i) => (
          <button
            key={i}
            data-testid={`quote-dot-${i}`}
            onClick={() => setIdx(i)}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-[#FFB800]' : 'bg-[#3D1A1A]'}`}
            aria-label={`Quote ${i + 1}`}
          />
        ))}
      </div>
    </div>
  </section>
);

const PlaylistCard = ({ playlist, span = 1 }) => (
  <Link
    to={`/playlist/${playlist.slug}`}
    data-testid={`playlist-card-${playlist.slug}`}
    className={`group relative aspect-square rounded-2xl overflow-hidden border border-[#3D1A1A] active:scale-[0.98] transition-transform ${span === 2 ? 'col-span-2 aspect-[2/1]' : ''}`}
  >
    <img src={playlist.cover_image} alt={playlist.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#120505] via-[#120505]/40 to-transparent" />
    <div className="absolute inset-0 p-4 flex flex-col justify-end">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB800] mb-1">
        {playlist.category_type === 'era' ? 'Era' : 'Mood'}
      </div>
      <h3 className="font-display text-xl sm:text-2xl text-[#FFF3E0] leading-tight drop-shadow-lg">{playlist.name}</h3>
      <p className="text-xs text-[#FFF3E0]/70 mt-1 line-clamp-1">{playlist.song_ids?.length || 0} songs</p>
    </div>
  </Link>
);

const EmptyStrip = ({ text }) => (
  <div className="rounded-2xl border border-[#3D1A1A] p-6 text-center text-sm text-[#BFA5A5] bg-[#220A0A]/40">
    {text}
  </div>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-2 gap-4">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="aspect-square rounded-2xl bg-[#220A0A] animate-pulse" />
    ))}
  </div>
);

export default Home;
